import { useCallback } from 'react';
import { rage, useObservable } from '~/rage';
import { isRgbPaintType, IVehicleBodyColor, VehiclePaintType } from '~/shared';
import {
	setActiveListIndex,
	setGroup,
	setIndexedColor,
	setOwnedVehiclePaint,
	setPearlColor,
	setRimsColor,
	useLayoutDispatch,
} from '../redux';

export const useVehiclePaintLoader = () => {
	const dispatch = useLayoutDispatch();
	const vehicleIndexedColors = useObservable(
		rage.vehiclePaintShop.getVehicleIndexedColors(),
	);
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);

	const loadVehiclePaint = useCallback(
		async (preview?: boolean) => {
			const value = await rage.vehiclePaintShop.getOwnedVehiclePaint();
			dispatch(setOwnedVehiclePaint(value));

			if (!value?.bodyColor) return;

			const { bodyColor, interiorColor } = value;
			let indexedColor: number | null = null;

			const handleColorSection = (
				bodyColor: IVehicleBodyColor,
				interiorColor: string,
			) => {
				if (isRgbPaintType(bodyColor.type)) {
					if (bodyColor.type === VehiclePaintType.Pearl) {
						const pearlMatch = pearlColors.find(
							(color) => color.index === bodyColor.pearlColor,
						);

						if (preview) {
							rage.vehiclePaintShop.previewBodyColor(
								bodyColor.color,
								bodyColor.type,
								pearlMatch ? pearlMatch.index : null,
							);
							rage.vehiclePaintShop.previewInteriorColor(interiorColor);
						}
					}
				} else {
					const indexedMatch = vehicleIndexedColors
						.filter((color) => color.type === bodyColor.type)
						.find((color) => color.index === bodyColor.color);

					if (indexedMatch) {
						indexedColor = indexedMatch.index;
					}
				}

				if (preview && bodyColor.type !== VehiclePaintType.Pearl) {
					rage.vehiclePaintShop.previewBodyColor(
						bodyColor.color,
						bodyColor.type,
						null,
					);
				}
			};

			handleColorSection(bodyColor, interiorColor);

			const applyChanges = () => {
				if (typeof indexedColor === 'number') {
					dispatch(setIndexedColor(indexedColor));
				}

				if (value.rimsColor) {
					dispatch(setRimsColor(value.rimsColor));

					if (preview) {
						rage.vehiclePaintShop.previewRimsColor(value.rimsColor);
					}
				}

				if (value.windowTint) {
					dispatch(setActiveListIndex(null));

					if (preview) {
						rage.vehiclePaintShop.previewWindowTint(value.windowTint);
					}
				}

				if (
					bodyColor.type === VehiclePaintType.Pearl &&
					typeof bodyColor.pearlColor === 'number'
				) {
					dispatch(setPearlColor(bodyColor.pearlColor));
				}

				dispatch(
					setGroup(paintGroups.find((group) => group.type === bodyColor.type)),
				);
			};

			applyChanges();
		},
		[vehicleIndexedColors, paintGroups, pearlColors],
	);

	return { loadVehiclePaint };
};
